import Vue from 'vue'
import JsonApiObject from '@/_models/JsonApi'

import UserModel from '@/views/User/models/user'
import ThreadModel from '@/views/Project/Comment/models/thread'
import ReactionModel from '@/views/Project/Comment/models/reaction'
import AttachmentModel from '@/views/Attachment/model/attachment'

import { reactionsAll } from '@/views/Project/Comment/models/reaction'

import lodash from 'lodash'
export default class CommentModel extends JsonApiObject {
    public constructor(data: any, included?) {
        super(data, included)
    }

    body
    bodyEdited
    deleted
    numReplies
    numWatchers
    createdAt
    updatedAt

    get thread(): ThreadModel {
        return this._relationships['thread']
            ? this._relationships['thread']
            : this.parent
            ? this.parent.thread
                ? this.parent.thread
                : null
            : null
    }

    get parent(): CommentModel {
        return this._relationships['parent'] ? this._relationships['parent'] : null
    }

    get createdBy(): UserModel {
        return this._relationships['createdBy'] ? this._relationships['createdBy'] : null
    }

    get replyUsers(): Array<UserModel> {
        return this._relationships['replyUsers'] ? this._relationships['replyUsers'] : []
    }

    get replies(): Array<CommentModel> {
        return this._relationships['replies'] ? this._relationships['replies'] : []
    }

    set replies(replies: Array<CommentModel>) {
        Vue.set(
            this._relationships,
            'replies',
            lodash.uniqBy(replies, '_id').sort(function (a, b) {
                return +new Date(a.createdAt) - +new Date(b.createdAt)
            })
        )
    }

    get reactions(): Array<ReactionModel> {
        return this._relationships['reactions'] ? this._relationships['reactions'] : []
    }

    set reactions(reactions: Array<ReactionModel>) {
        Vue.set(this._relationships, 'reactions', lodash.uniqBy(reactions, '_id'))
    }

    get grouppedReactions() {
        return lodash.groupBy(this.reactions, 'reaction')
    }

    userReacted(reaction) {
        return this.grouppedReactions && this.grouppedReactions[reaction]
            ? this.grouppedReactions[reaction].find((item) => {
                  return item.user.isCurrentUser
              })
            : false
    }

    reactionEmoji(reaction) {
        return reactionsAll[reaction]
    }

    get attachments(): Array<AttachmentModel> {
        return this._relationships['attachments'] ? this._relationships['attachments'] : []
    }

    set attachments(attachments: Array<AttachmentModel>) {
        this._relationships['attachments'] = attachments
    }

    async toggleDelete(val: boolean) {
        let data = {
            id: this._id,
            type: this._type,
            attributes: {
                deleted: val,
            },
        }

        return await this.patch(data)
    }

    async updateBody(val: string | null) {
        let data = {
            id: this._id,
            type: this._type,
            attributes: {
                body: val,
            },
        }

        return await this.patch(data)
    }

    async patch(
        data,
        include = ['thread', 'parent', 'createdBy', 'reactions.user', 'attachments']
    ) {
        let query = {
            include: include,
            body: {
                data: data,
            },
        }

        let res = await Vue.prototype.$vbit.patch('comment', `comments/${this._id}`, query)

        const replies = this.replies

        this.initalize(res.data, res.included)
        this.replies = replies

        return this
    }

    async get(include = ['thread', 'parent', 'createdBy', 'reactions.user', 'attachments']) {
        let query = {
            include: include,
        }
        let res = await Vue.prototype.$vbit.get('comment', `comments/${this._id}`, query)
        const replies = this.replies

        this.initalize(res.data, res.included)
        this.replies = replies
        return this
    }

    async loadReplies(filters, include = ['parent', 'createdBy', 'reactions.user', 'attachments']) {
        let query = {
            filters: filters,
            include: include,
        }

        let res = await Vue.prototype.$vbit.get('comment', `comments`, query)

        let comments = res.data.map((item) => new CommentModel(item, res.included))

        this.replies = [...this.replies, ...comments]
        return comments
    }

    async loadReply(replyID, include = ['parent', 'createdBy', 'reactions.user', 'attachments']) {
        let query = {
            include: include,
        }

        let res = await Vue.prototype.$vbit.get('comment', `comments/${replyID}`, query)

        let reply = new CommentModel(res.data, res.included)

        this.replies = [reply, ...this.replies]
        return reply
    }

    async reply(body, include = ['parent', 'createdBy', 'reactions.user', 'attachments']) {
        let query = {
            include: include,
            body: {
                data: {
                    type: 'comment',
                    attributes: {
                        body: body,
                    },
                    relationships: {
                        parent: {
                            data: {
                                id: this._id,
                                type: this._type,
                            },
                        },
                    },
                },
            },
        }
        let res = await Vue.prototype.$vbit.post('comment', `comments`, query)

        let comment = new CommentModel(res.data, res.included)
        this.replies = [...this.replies, comment]
        this.numReplies = this.numReplies + 1
        return comment
    }

    static async get(
        commentId: string,
        include = [
            'thread',
            'parent.createdBy',
            'parent.reactions.user',
            'parent.attachments',
            'parent.thread',
            'createdBy',
            'reactions.user',
            'attachments',
        ]
    ) {
        let query = {
            include: include,
        }
        let res = await Vue.prototype.$vbit.get('comment', `comments/${commentId}`, query)
        return new CommentModel(res.data, res.included)
    }
}
